import { observer } from "mobx-react-lite";
import Box from "@mui/material/Box"
import Paper from "@mui/material/Paper"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useTheme } from "@mui/material/styles"
import ProposalAttachmentsPanel from "./ProposalAttachmentsPanel";
import ProposalAttachmentsTrigger from "./ProposalAttachmentsTrigger";
import ProposalSectionTiming from "./section/ProposalSectionTiming";
import ProposalSectionObjectives from "./section/ProposalSectionObjectives";
import ProposalSectionExpectations from "./section/ProposalSectionExpectations";
import ProposalSectionHeader from "./section/ProposalSectionHeader";
import ProposalSectionServices from "./section/services/ProposalSectionServices";
import ProposalSectionPricing from "./section/pricing/ProposalSectionPricing";
import useStore from "reusable/mobx-store-utils";
import ProposalsViewProposalStore, { ProposalsViewProposalStoreProvider } from "./ProposalsViewProposalStore";
import useInitialMount from "reusable/hooks/useInitialMount";
import SkeletonLoader from "reusable/mui/skeleton/SkeletonLoader";
import ProposalSectionAcceptance from "./section/acceptance/ProposalSectionAcceptance";
import { ProposalDto } from "@gap/backend/app-services/proposal/models/ProposalDto";
import { useOrgGuardStore } from "features/main/main-router/org-guard/OrgGuardStore";
import { useProposalsViewStore } from "../ProposalsViewStore";
import ProposalSignedConfirmation from "./confirmation/ProposalSignedConfirmation";
import PrintableRegion, { FooterElement } from "reusable/printing/PrintableRegion";
import { usePrintStore } from "reusable/printing/PrintStore";
import { themeConfig } from '@gap/shared/theme';
import { IosShareOutlined } from "@mui/icons-material";
import OnlyForOrgType from "reusable/containers/OnlyForOrgType";
import ButtonWithProgress from "reusable/mui/progress-button/ButtonWithProgress";
import DownloadPdfButton from "reusable/printing/DownloadPdfButton";

export default observer(function ProposalsViewProposal({ proposal, editable, embedded, hideActions, ...props }: ProposalsViewProposalProps) {
   const printStore = usePrintStore()
   const orgGuardStore = useOrgGuardStore()
   const proposalsViewStore = useProposalsViewStore()
   const proposalsViewProposalStore = useStore((sp) => new ProposalsViewProposalStore(sp), { proposal, orgGuardStore, proposalsViewStore, printStore })
   const { onServiceAgreementChange, serviceIdsToIncludInPricing } = proposalsViewProposalStore
   const theme = useTheme()
   const isMd = useMediaQuery(theme.breakpoints.up('md'))

   useInitialMount(async () => {
      await proposalsViewProposalStore.fetch()
   })

   if (proposalsViewProposalStore.loader.isLoading) {
      return <SkeletonLoader preserveContentBounds="empty-space" loading={true} />
   }

   const hasAttachments = !!proposal.step5?.attachments?.length
   // When the advisor is previewing/editing (editable=true), attachments are managed
   // via the sidebar's Add Attachment flow, so suppress the read-view panel here.
   const showAttachments = !editable && hasAttachments
   // Embedded contexts suppress the right-hand sidebar and surface attachments via an
   // inline button + drawer alongside Share, so the proposal scrolls inside the parent's appbar.
   const showSidebar = showAttachments && isMd && !embedded
   const showInlineAttachments = showAttachments && !!embedded

   const proposalContent = <PrintableRegion footer={{
      left: [
         'Proposal',
         proposalsViewStore.clientOrg?.name,
      ].join(
         `${FooterElement.enSpace}|${FooterElement.enSpace}`
      )
   }} sx={{
      display: 'flex',
      gap: 2,
      flexDirection: 'column',
      '& ul': {
         paddingInlineStart: '1.5em',
         textIndent: '.125em',
      },
   }}>
      {(proposalsViewProposalStore.currentSignee?.agreedAt || 0) > proposalsViewProposalStore.timeViewed ? <>
         <ProposalSignedConfirmation />
      </> : <>
         {proposalsViewStore.clientOrg && <ProposalSectionHeader
            {...{ proposal }}
            advisorLogoUrl={proposalsViewProposalStore.displayedLogoUrl}
            sectionProps={{ editable, onEdit: props.goToStep1 }}
         />}
         <ProposalSectionObjectives
            {...{ proposal }}
            sectionProps={{ editable, onEdit: props.goToStep4 }}
         />
         <ProposalSectionExpectations
            {...{ proposal }}
            sectionProps={{ editable, onEdit: props.goToStep4 }}
         />
         <ProposalSectionTiming
            {...{ proposal }}
            sectionProps={{ editable, onEdit: props.goToStep4 }}
         />
         <ProposalSectionServices
            {...{ proposal, onServiceAgreementChange }}
            sectionProps={{ editable, onEdit: props.goToStep2 }}
         />
         <ProposalSectionPricing
            {...{ proposal, showPreviewHints: editable }}
            sectionProps={{ editable, onEdit: props.goToStep3 }}
            filteredServiceIds={serviceIdsToIncludInPricing}
         />
         <ProposalSectionAcceptance
            {...{ proposal }}
            sectionProps={{ editable, onEdit: props.goToStep4 }}
         />
      </>}
   </PrintableRegion>

   const actionRows = <>
      <OnlyForOrgType orgType="advisor" and={!!proposalsViewStore.proposal?.acceptedAt}>
         <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            {showInlineAttachments && <ProposalAttachmentsTrigger proposal={proposal} inline />}
            <ButtonWithProgress variant="contained" icon={<IosShareOutlined />} onClick={proposalsViewStore.shareDialogModalState.open} loading={proposalsViewStore.shareDialogLoader.isLoading}>Share</ButtonWithProgress>
         </Box>
      </OnlyForOrgType>
      <OnlyForOrgType orgType="client" and={!!proposalsViewStore.proposal?.acceptedAt && !!proposalsViewProposalStore.proposalDownloadUrl}>
         <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
            {showInlineAttachments && <ProposalAttachmentsTrigger proposal={proposal} inline />}
            <DownloadPdfButton docTitle={proposalsViewStore.proposalTitle} docId={proposal.id} documentUrl={proposalsViewProposalStore.proposalDownloadUrl} />
         </Box>
      </OnlyForOrgType>
   </>

   const workspace = <Box sx={{
      flex: embedded ? 'none' : 1,
      minWidth: 0,
      overflowY: embedded ? 'visible' : 'auto',
      p: embedded ? 0 : { xs: 2, sm: 8 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
   }}>
      <Box sx={{ width: '100%', maxWidth: themeConfig.breakpoints.values.md }}>
         {!hideActions && actionRows}
         <Paper elevation={12} sx={{
            mb: 2,
            borderRadius: { xs: '24px', sm: '48px' },
            overflow: 'hidden',
         }}>
            {proposalContent}
         </Paper>
      </Box>
   </Box>

   return <ProposalsViewProposalStoreProvider store={proposalsViewProposalStore}>
      {showSidebar ? (
         <Box sx={{
            flex: embedded ? 'none' : 1,
            minHeight: 0,
            display: 'flex',
            overflow: embedded ? 'visible' : 'hidden',
            width: '100%',
            alignItems: embedded ? 'flex-start' : 'stretch',
         }}>
            {workspace}
            <Box sx={{ width: 410, flexShrink: 0, height: embedded ? 'auto' : '100%' }}>
               <ProposalAttachmentsPanel proposal={proposal} />
            </Box>
         </Box>
      ) : (
         workspace
      )}
   </ProposalsViewProposalStoreProvider>
})

type ProposalsViewProposalProps = {
   proposal: ProposalDto,
   /** There are some nuances if this is an advisor previewing their proposal */
   editable?: boolean,
   /** When true, render in document-flow (no internal scroll, no height lock).
    *  Use when a parent route already provides the parent's appbar + page scroll. */
   embedded?: boolean,
   /** Suppress the action row (Share / Download). Used by the Client Documents
    *  surface, which is read-only and provides its own breadcrumb navigation. */
   hideActions?: boolean,
   goToStep1?: () => any,
   goToStep2?: () => any,
   goToStep3?: () => any,
   goToStep4?: () => any,
}
